import type { TFunction } from 'i18next';
import { getRadialMenuPositions } from './radialMenu';


export interface RadialMenuOverlayProps {
  t: TFunction;
  handleRotateHouse: () => Promise<void>;
  handleAbandonHouse: () => Promise<void>;
  /** The marketplace sale lives here, among the rarely used options. */
  handleSellHouse: () => Promise<void>;
  menuPosition: { x: number; y: number };
  onClose: () => void;
}

export default function RadialMenuOverlay({
  t,
  handleRotateHouse,
  handleAbandonHouse,
  handleSellHouse,
  menuPosition,
  onClose,
}: RadialMenuOverlayProps) {
  // Define all available menu options
  const allOptions: Array<{ key: string; icon?: string; emoji?: string; text: string; onClick: () => Promise<void> }> = [
    {
      key: 'rotate',
      icon: '/ui/house-options/rotate-house.webp',
      text: t('Flip'),
      onClick: handleRotateHouse,
    },
    {
      key: 'sell',
      emoji: '💎',
      text: t('Sell for TON'),
      onClick: handleSellHouse,
    },
    {
      key: 'abandon',
      icon: '/ui/house-options/abandon-house.webp',
      text: t('Abandon house...'),
      onClick: handleAbandonHouse,
    },
  ];

  // Get all available options
  const visibleOptions = allOptions;
  const positions = getRadialMenuPositions(visibleOptions.length);

  return (
    <>
      <div
        className="fixed inset-0 z-[3000] bg-black-alpha-60"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div className="pointer-events-none fixed inset-0 z-[3001]">
        {visibleOptions.map((item, index) => {
          const position = positions[index];
          if (!position) return null;
          const x = menuPosition.x + position.xOffset;
          const y = menuPosition.y + position.yOffset;

          return (
            <div
              key={item.key}
              className="pointer-events-auto fixed z-[3002]"
              style={{
                left: `${x}px`,
                top: `${y}px`,
                animation: `fadeInScale 0.2s ease ${index * 0.1}s both`,
              }}
            >
              <div className="relative flex h-[30px] items-center">
                <div className="relative z-[2] mr-[-11px] flex h-[22px] items-center justify-center">
                  {item.icon ? (
                    <img
                      src={item.icon}
                      alt=""
                      className="block h-[22px] w-auto object-contain"
                    />
                  ) : (
                    <span className="text-[18px] leading-[22px]">{item.emoji}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onClick();
                    onClose();
                  }}
                  className="box-border flex h-[22px] cursor-pointer items-center justify-center rounded-[11px] border border-light-button-border bg-white px-2xl text-[14px] font-semibold whitespace-nowrap text-light-button-text shadow-white-soft"
                  style={{
                    width: position.xOffset !== 0 ? '136px' : 'auto',
                  }}
                >
                  {item.text}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </>
  );
}
